/**
 * ApprovalIntegrationPanel
 * 인사팀·재무 공용 결재 연동 탭 패널.
 * props:
 *   loadApprovals  : () => Promise<ApprovalResponse[]>
 *   infoBannerText : string
 */
import TablePaginator from "../common/TablePaginator";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../util/api";
import {
    Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogContent,
    Divider, IconButton, Paper, Stack, TextField,
    Tooltip, Typography,
} from "@mui/material";
import AccessTimeRoundedIcon      from "@mui/icons-material/AccessTimeRounded";
import AssignmentRoundedIcon       from "@mui/icons-material/AssignmentRounded";
import AttachFileRoundedIcon       from "@mui/icons-material/AttachFileRounded";
import BeachAccessRoundedIcon      from "@mui/icons-material/BeachAccessRounded";
import CancelRoundedIcon           from "@mui/icons-material/CancelRounded";
import ActionButton from "../common/ActionButton";
import CheckCircleRoundedIcon      from "@mui/icons-material/CheckCircleRounded";
import ChevronRightRoundedIcon     from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon            from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon         from "@mui/icons-material/DownloadRounded";
import FlightRoundedIcon           from "@mui/icons-material/FlightRounded";
import ImageRoundedIcon            from "@mui/icons-material/ImageRounded";
import InsertDriveFileRoundedIcon  from "@mui/icons-material/InsertDriveFileRounded";
import PauseCircleRoundedIcon      from "@mui/icons-material/PauseCircleRounded";
import PaymentsRoundedIcon         from "@mui/icons-material/PaymentsRounded";
import PictureAsPdfRoundedIcon     from "@mui/icons-material/PictureAsPdfRounded";
import ShoppingCartRoundedIcon     from "@mui/icons-material/ShoppingCartRounded";
import { formatFileSize as formatSize } from "../../util/numberFormat";

import dayjs           from "dayjs";
import "dayjs/locale/ko";
import { approvalApi } from "../../page/approval/api/approvalApi";
import { ds }          from "../../styles/designSystem";
import { runApiAction, serverMsgOr } from "../../util/apiFeedback";
import LoadingDots from "../common/LoadingDots";
import ComboBox from "../common/ComboBox";
import FormField from "../common/FormField";
import { confirm as confirmDialog } from "../../util/confirmDialog";

dayjs.locale("ko");

const BASE      = process.env.REACT_APP_API_REAL_URL;
const PAGE_SIZE = 5;

/* ── 공통 메타 ───────────────────────────────────── */
const STATUS_META = {
    PENDING:     { label: "대기",  color: "#94a3b8", bg: "#f1f5f9" },
    IN_PROGRESS: { label: "진행",  color: "#2563eb", bg: "#eff6ff" },
    APPROVED:    { label: "승인",  color: "#16a34a", bg: "#f0fdf4" },
    REJECTED:    { label: "반려",  color: "#dc2626", bg: "#fef2f2" },
    HELD:        { label: "보류",  color: "#d97706", bg: "#fffbeb" },
};
const LINE_STATUS_META = {
    WAITING:  { label: "대기",   color: "#94a3b8" },
    PENDING:  { label: "결재중", color: "#2563eb" },
    APPROVED: { label: "승인",   color: "#16a34a" },
    REJECTED: { label: "반려",   color: "#dc2626" },
    HELD:     { label: "보류",   color: "#d97706" },
};
const FORM_TYPES = [
    { type: "VACATION",      label: "휴가신청서",   icon: <BeachAccessRoundedIcon     sx={{ fontSize: 32 }} />, color: "#3b82f6" },
    { type: "PAYMENT",       label: "대금지급요청", icon: <PaymentsRoundedIcon        sx={{ fontSize: 32 }} />, color: "#10b981" },
    { type: "PURCHASE",      label: "구매품의서",   icon: <ShoppingCartRoundedIcon     sx={{ fontSize: 32 }} />, color: "#f59e0b" },
    { type: "BUSINESS_TRIP", label: "출장신청서",   icon: <FlightRoundedIcon           sx={{ fontSize: 32 }} />, color: "#3b82f6" },
    { type: "OVERTIME",      label: "초과근무신청", icon: <AccessTimeRoundedIcon       sx={{ fontSize: 32 }} />, color: "#ef4444" },
    { type: "HOLIDAY_WORK",  label: "휴일근무신청", icon: <AssignmentRoundedIcon       sx={{ fontSize: 32 }} />, color: "#9333ea" },
    { type: "OTHER",         label: "기타결재",     icon: <AssignmentRoundedIcon       sx={{ fontSize: 32 }} />, color: "#6b7280" },
];

/* ── 유틸 ─────────────────────────────────────────── */
const COMPLETED = new Set(["APPROVED", "REJECTED", "HELD"]);
const isCompleted = (a) => COMPLETED.has(a.status);
const isMyTurn   = (a, myId) =>
    !!myId && !isCompleted(a) && (a.lines || []).some(l => l.approverId === myId && l.status === "PENDING");

function fileIcon(ct) {
    if (!ct) return <InsertDriveFileRoundedIcon />;
    if (ct.includes("pdf")) return <PictureAsPdfRoundedIcon sx={{ color: "#ef4444" }} />;
    if (ct.startsWith("image/")) return <ImageRoundedIcon sx={{ color: "#3b82f6" }} />;
    return <InsertDriveFileRoundedIcon sx={{ color: "#6b7280" }} />;
}
const toComma     = (v) => { const n = String(v || "").replace(/,/g, "").replace(/[^\d]/g, ""); return n ? Number(n).toLocaleString("ko-KR") : ""; };
const fmtDatetime = (v) => v ? dayjs(v).format("YYYY.MM.DD HH:mm") : "—";
const fmtDate     = (v) => v ? dayjs(v).format("YYYY.MM.DD") : "—";
function durationStr(s, e) {
    if (!s || !e) return null;
    const mins = dayjs(e).diff(dayjs(s), "minute");
    if (mins <= 0) return null;
    const parts = [];
    const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60), m = mins % 60;
    if (d) parts.push(`${d}일`);
    if (h) parts.push(`${h}시간`);
    if (m) parts.push(`${m}분`);
    return parts.join(" ");
}
function getRowSummary(type, form) {
    const d = form || {};
    switch (type) {
        case "VACATION":
            if (d.vacationType === "반차") return `반차 · ${fmtDate(d.startDate)}`;
            if (d.startDate && d.endDate)  return `${fmtDate(d.startDate)} ~ ${fmtDate(d.endDate)}`;
            return d.vacationType || "";
        case "PAYMENT":
        case "PURCHASE": return d.amount ? `${toComma(d.amount)} 원` : "";
        case "BUSINESS_TRIP": return d.destination || "";
        case "OVERTIME": return d.date ? fmtDate(d.date) : "";
        case "HOLIDAY_WORK": return d.date ? `${fmtDate(d.date)}${d.hours ? ` · ${d.hours}시간` : ""}` : "";
        default: return "";
    }
}

/* ── StatusBadge ─────────────────────────────────── */
function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.PENDING;
    return (
        <Box sx={{ display: "inline-flex", alignItems: "center", px: 1, py: 0.2,
            borderRadius: 1, bgcolor: m.bg, color: m.color, fontSize: 12, fontWeight: 700 }}>
            {m.label}
        </Box>
    );
}

/* ── FormFields ──────────────────────────────────── */
function FormFields({ type, form, applicant }) {
    const data = form || {};
    const rBox = (label, value, multi = false) => (
        <Box key={label}>
            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.3 }}>{label}</Typography>
            <Typography sx={{ fontSize: 14, color: "#1e293b", p: "8px 12px", bgcolor: "#f8fafc",
                border: "1px solid #e2e8f0", borderRadius: 1.5,
                minHeight: multi ? 80 : "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {value || "—"}
            </Typography>
        </Box>
    );
    const sBox = (text, color = "#7e22ce", bg = "#fdf4ff", border = "#bfdbfe") => (
        <Box sx={{ px: 1.5, py: 1, bgcolor: bg, borderRadius: 1.5, border: `1px solid ${border}` }}>
            <Typography sx={{ fontSize: 12, color, fontWeight: 700 }}>{text}</Typography>
        </Box>
    );
    const applicantRow = (
        <Box key="applicant">
            <Typography sx={{ fontSize: 12, color: "#64748b", mb: 0.3 }}>신청자</Typography>
            <Box sx={{ p: "8px 12px", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 1.5,
                display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar sx={{ width: 22, height: 22, fontSize: 11, bgcolor: "#2563eb" }}>
                    {applicant?.[0] || "?"}
                </Avatar>
                <Typography sx={{ fontSize: 14, color: "#1e293b", fontWeight: 600 }}>{applicant || "—"}</Typography>
            </Box>
        </Box>
    );
    switch (type) {
        case "VACATION": {
            const vt = data.vacationType, isHalf = vt === "반차", isComp = vt === "대체휴무", isHourly = vt === "시간연차";
            const isDt = vt && vt !== "연차" && !isHalf && !isComp && !isHourly;
            const dur = isDt ? durationStr(data.startDate, data.endDate) : null;
            return (
                <Stack spacing={1.5}>
                    {applicantRow}{rBox("휴가 종류", vt)}
                    {isHalf ? (<>{rBox("날짜", fmtDate(data.startDate))}{rBox("반차 구분", data.halfDayType)}</>)
                    : isComp ? (<>{rBox("보상 방식", data.redeemType === "CASH" ? "현금 정산" : "휴가 사용")}{rBox("사용 단위", data.unit)}{data.redeemType !== "CASH" && rBox("날짜", fmtDate(data.startDate))}{data.sourceApprovalText && rBox("대체근무 결재", data.sourceApprovalText)}</>)
                    : isHourly ? (<>{rBox("날짜", fmtDate(data.startDate))}<Stack direction="row" spacing={1}>{rBox("시작 시간", data.startTime)}{rBox("종료 시간", data.endTime)}</Stack>{data.hours > 0 && sBox(`${data.hours}시간 연차 사용`, "#0369a1", "#f0f9ff", "#bae6fd")}</>)
                    : isDt  ? (<Stack direction="row" spacing={1}>{rBox("시작 일시", fmtDatetime(data.startDate))}{rBox("종료 일시", fmtDatetime(data.endDate))}</Stack>)
                    :         (<Stack direction="row" spacing={1}>{rBox("시작일", fmtDate(data.startDate))}{rBox("종료일", fmtDate(data.endDate))}</Stack>)}
                    {dur && sBox(`${fmtDatetime(data.startDate)} ~ ${fmtDatetime(data.endDate)}  ·  ${dur}`, "#0369a1", "#f0f9ff", "#bae6fd")}
                    {rBox("사유", data.reason, true)}
                </Stack>
            );
        }
        case "HOLIDAY_WORK": return (
            <Stack spacing={1.5}>{applicantRow}{rBox("근무일", fmtDate(data.date))}
                <Stack direction="row" spacing={1}>{rBox("시작 시간", data.startTime)}{rBox("종료 시간", data.endTime)}</Stack>
                {data.hours > 0 && sBox(`근무시간 ${data.hours}시간 · 대체휴무 1일(8h) 적립`, "#6d28d9", "#f5f3ff", "#ddd6fe")}
                {rBox("사유", data.reason, true)}</Stack>
        );
        case "PAYMENT": return (
            <Stack spacing={1.5}>{applicantRow}{rBox("거래처명", data.vendor)}
                {rBox("금액 (원)", data.amount ? `${toComma(data.amount)} 원` : "")}
                {rBox("지급 예정일", data.paymentDate)}{rBox("지급 내용", data.description, true)}</Stack>
        );
        case "PURCHASE": return (
            <Stack spacing={1.5}>{applicantRow}{rBox("품목명", data.item)}{rBox("수량", data.quantity)}
                {rBox("금액 (원)", data.amount ? `${toComma(data.amount)} 원` : "")}
                {rBox("구매 사유", data.reason, true)}
                {(data.vendorUrl || data.vendorName) && rBox("구매처", data.vendorUrl || data.vendorName)}</Stack>
        );
        case "BUSINESS_TRIP": {
            const dur = durationStr(data.startDate, data.endDate);
            return (
                <Stack spacing={1.5}>{applicantRow}{rBox("출장지", data.destination)}
                    <Stack direction="row" spacing={1}>{rBox("출발 일시", fmtDatetime(data.startDate))}{rBox("복귀 일시", fmtDatetime(data.endDate))}</Stack>
                    {dur && sBox(`${fmtDatetime(data.startDate)} ~ ${fmtDatetime(data.endDate)}  ·  ${dur}`)}
                    {rBox("출장 목적", data.purpose, true)}{rBox("예상 비용 (원)", data.cost ? `${toComma(data.cost)} 원` : "")}</Stack>
            );
        }
        case "OVERTIME": {
            const dur = data.date && data.startTime && data.endTime
                ? durationStr(`${data.date}T${data.startTime}`, `${data.date}T${data.endTime}`) : null;
            return (
                <Stack spacing={1.5}>{applicantRow}{rBox("날짜", fmtDate(data.date))}
                    <Stack direction="row" spacing={1}>{rBox("시작 시간", data.startTime)}{rBox("종료 시간", data.endTime)}</Stack>
                    {dur && sBox(`${data.date}  ${data.startTime} – ${data.endTime}  ·  ${dur}`, "#be123c", "#fff1f2", "#fecdd3")}
                    {rBox("사유", data.reason, true)}</Stack>
            );
        }
        default: return (<Stack spacing={1.5}>{applicantRow}{rBox("제목", data.title)}{rBox("내용", data.content, true)}</Stack>);
    }
}

/* ── ApprovalLineBar ─────────────────────────────── */
function ApprovalLineBar({ lines = [], approval }) {
    if (!lines?.length) return null;
    const nodes = [
        { label: approval?.submitterName || "—", subLabel: "신청", status: "SUBMIT",
          initial: approval?.submitterName?.[0] || "?", color: "#2563eb" },
        ...lines.map((l, idx) => {
            const m = LINE_STATUS_META[l.status] || LINE_STATUS_META.WAITING;
            return { label: l.approverName || "—", subLabel: m.label, status: l.status,
                initial: l.approverName?.[0] || "?", color: m.color,
                isFinal: idx === lines.length - 1, isActive: l.status === "PENDING",
                comment: l.comment, actionAt: l.actionAt };
        }),
    ];
    const connColor = (s) => (s === "APPROVED" ? "#16a34a" : "#e2e8f0");
    const nodeStyle = (s, active) => ({
        width: 52, height: 52, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: s === "SUBMIT" ? "#2563eb" : s === "APPROVED" ? "#16a34a" : s === "REJECTED" ? "#dc2626"
               : s === "HELD" ? "#d97706" : active ? "#2563eb" : "#e2e8f0",
        color: s === "WAITING" ? "#94a3b8" : "#fff", fontWeight: 700, fontSize: 15,
        border: active ? "2px solid #93c5fd" : "2px solid transparent",
        boxShadow: active ? "0 0 0 4px rgba(37,99,235,0.15)" : "none",
    });
    return (
        <Box sx={{ py: 1 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                {nodes.map((node, idx) => (
                    <React.Fragment key={`${node.label}-${idx}`}>
                        <Box sx={{ width: 130, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <Tooltip title={node.comment || ""} arrow>
                                <Box sx={{ ...nodeStyle(node.status, node.isActive), cursor: node.comment ? "help" : "default" }}>
                                    {node.initial}
                                </Box>
                            </Tooltip>
                            <Typography sx={{ mt: 1, fontSize: 12, color: node.color, fontWeight: node.isActive ? 700 : 600,
                                textAlign: "center", maxWidth: 110, lineHeight: 1.2 }} noWrap>{node.label}</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 0.4 }}>
                                {node.isFinal && <Typography sx={{ fontSize: 10, color: "#f59e0b", fontWeight: 700 }}>최종</Typography>}
                                <Typography sx={{ fontSize: 11, color: node.color }}>{node.subLabel}</Typography>
                            </Stack>
                            {node.actionAt && (
                                <Typography sx={{ mt: 0.4, fontSize: 10, color: "#94a3b8", textAlign: "center" }}>
                                    {dayjs(node.actionAt).format("MM/DD HH:mm")}
                                </Typography>
                            )}
                        </Box>
                        {idx < nodes.length - 1 && (
                            <Box sx={{ flex: 1, minWidth: 40, height: 3, mt: "24px", mx: 1,
                                bgcolor: idx === 0 ? "#e2e8f0" : connColor(nodes[idx].status), borderRadius: 999 }} />
                        )}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
}

/* ── ApprovalDetail ──────────────────────────────── */
export function ApprovalDetail({ approval, myId, onRefresh, onClose, financeConfirm, accounts = [] }) {
    const [commentText,  setCommentText]  = useState("");
    const [actionDialog, setActionDialog] = useState(null);
    const [uploading,    setUploading]    = useState(false);
    const [confirmAccountId, setConfirmAccountId] = useState("");   // 재무 지출확정 시 자금일보 반영 계좌
    const fileInputRef = useRef();

    const done          = isCompleted(approval);
    const myLine        = approval.lines?.find(l => l.approverId === myId && l.status === "PENDING");
    const isMine        = isMyTurn(approval, myId);
    const isFinance     = approval.type === "PAYMENT" || approval.type === "PURCHASE";
    const isFinalFin    = Boolean(isFinance && myLine && myLine.stepOrder === approval.totalSteps);
    const typeInfo      = FORM_TYPES.find(f => f.type === approval.type);
    // 재무 최종 지출확정: 계좌를 골라 자금일보 자동 반영까지 처리 (financeConfirm 이 있을 때만)
    const financeApprove = actionDialog === "approve" && isFinalFin && typeof financeConfirm === "function";

    const handleAction = () => runApiAction({
        request: () => {
            if (actionDialog === "approve") {
                if (financeApprove) return financeConfirm(approval.id, confirmAccountId || null);
                return approvalApi.approve(approval.id, commentText);
            }
            else if (actionDialog === "reject")  return approvalApi.reject(approval.id, commentText);
            else                                 return approvalApi.hold(approval.id, commentText);
        },
        successMsg: actionDialog === "approve" ? "승인했습니다." : actionDialog === "reject" ? "반려했습니다." : "보류했습니다.",
        errorMsg: (msg) => "처리 실패: " + msg,
        onSuccess: () => { setActionDialog(null); setCommentText(""); setConfirmAccountId(""); onRefresh(); onClose(); },
    });
    const handleUpload = async (files) => {
        await runApiAction({
            request: async () => {
                let lastResponse = null;
                for (const f of files) lastResponse = await approvalApi.uploadAttachment(approval.id, f);
                return lastResponse;
            },
            errorMsg: serverMsgOr("파일 업로드 실패"),
            successMsg: "파일 첨부됨",
            setLoading: setUploading,
            onSuccess: onRefresh,
        });
    };
    const handleDeleteAtt = async (id) => {
        if (!(await confirmDialog({ message: "첨부파일을 삭제하시겠습니까?" }))) return;
        return runApiAction({
            request: () => approvalApi.deleteAttachment(id),
            errorMsg: serverMsgOr("삭제 실패"),
            onSuccess: onRefresh,
        });
    };

    return (
        <Box>
            {/* 헤더 */}
            <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={2}>
                <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: `${typeInfo?.color}18`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: typeInfo?.color, flexShrink: 0 }}>
                    {typeInfo?.icon && React.cloneElement(typeInfo.icon, { sx: { fontSize: 22 } })}
                </Box>
                <Box flex={1}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.3} flexWrap="wrap">
                        <StatusBadge status={approval.status} />
                        <Chip label={typeInfo?.label} size="small"
                            sx={{ fontSize: 11, height: 20, bgcolor: "#f1f5f9", color: "#475569" }} />
                    </Stack>
                    <Typography sx={{ fontWeight: 700, fontSize: 22, color: "#0f172a", lineHeight: 1.3 }}>{approval.title}</Typography>
                    <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.5 }}>
                        {approval.submitterName}{approval.submitterPosition && ` · ${approval.submitterPosition}`}
                        {approval.submitterDept && ` · ${approval.submitterDept}`}
                        {" · "}{dayjs(approval.createdAt).format("YYYY-MM-DD HH:mm")}
                    </Typography>
                </Box>
            </Stack>

            {/* 결재 라인 */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1.5, border: "1px solid #e2e8f0", mb: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", mb: 1 }}>결재 라인</Typography>
                <ApprovalLineBar lines={approval.lines} approval={approval} />

                {/* 결재자 상세 테이블 */}
                {approval.lines?.length > 0 && (
                    <Box sx={{ mt: 1.5, borderRadius: 1.5, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                        {/* 신청자 행 */}
                        <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.9,
                            bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0", gap: 1 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#2563eb", flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#2563eb", width: 50, flexShrink: 0 }}>신청자</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b", flex: 1 }}>
                                {approval.submitterName}
                                {approval.submitterPosition && ` · ${approval.submitterPosition}`}
                                {approval.submitterDept && ` · ${approval.submitterDept}`}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", flexShrink: 0 }}>
                                {dayjs(approval.createdAt).format("YYYY-MM-DD HH:mm")}
                            </Typography>
                        </Box>
                        {/* 결재자 행들 */}
                        {approval.lines.map((line, idx) => {
                            const lm = LINE_STATUS_META[line.status] || LINE_STATUS_META.WAITING;
                            const isFinal = idx === approval.lines.length - 1;
                            return (
                                <Box key={line.id ?? idx} sx={{ display: "flex", alignItems: "center",
                                    px: 1.5, py: 0.9, gap: 1,
                                    borderBottom: idx < approval.lines.length - 1 ? "1px solid #f1f5f9" : "none",
                                    bgcolor: line.status === "PENDING" ? "#eff6ff" : "transparent" }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: lm.color, flexShrink: 0 }} />
                                    <Box sx={{ width: 50, flexShrink: 0 }}>
                                        <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                                            {idx + 1}단계{isFinal ? " · 최종" : ""}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b", flex: 1 }}>
                                        {line.approverName}
                                        {line.approverPosition && ` · ${line.approverPosition}`}
                                        {line.approverDept && ` · ${line.approverDept}`}
                                    </Typography>
                                    <Box sx={{ px: 0.75, py: 0.15, borderRadius: 0.75, fontSize: 11,
                                        fontWeight: 700, bgcolor: `${lm.color}18`, color: lm.color, flexShrink: 0 }}>
                                        {lm.label}
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8", flexShrink: 0, minWidth: 90, textAlign: "right" }}>
                                        {line.actionAt ? dayjs(line.actionAt).format("MM/DD HH:mm") : "—"}
                                    </Typography>
                                    {line.comment && (
                                        <Tooltip title={line.comment} arrow>
                                            <Typography sx={{ fontSize: 11, color: "#64748b", maxWidth: 120,
                                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                💬 {line.comment}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        })}
                        {/* 최종 처리 시각 요약 */}
                        {(() => {
                            const lastAction = [...(approval.lines || [])]
                                .filter(l => l.actionAt)
                                .pop();
                            if (!lastAction) return null;
                            const lm = LINE_STATUS_META[lastAction.status] || LINE_STATUS_META.WAITING;
                            return (
                                <Box sx={{ px: 1.5, py: 0.75, bgcolor: `${lm.color}08`,
                                    borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography sx={{ fontSize: 11, color: "#64748b" }}>최종 처리</Typography>
                                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: lm.color }}>
                                        {lastAction.approverName} · {lm.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: "auto" }}>
                                        {dayjs(lastAction.actionAt).format("YYYY-MM-DD HH:mm")}
                                    </Typography>
                                </Box>
                            );
                        })()}
                    </Box>
                )}
            </Paper>

            {isFinalFin && (
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: "1px solid #bbf7d0", bgcolor: "#f0fdf4", mb: 2 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>재무 자동 반영 대상</Typography>
                    <Typography sx={{ fontSize: 12, color: "#166534", mt: 0.5 }}>
                        최종 승인하면 재무 수입/지출 내역에 지출 거래가 자동 등록됩니다.
                    </Typography>
                </Paper>
            )}

            {/* 결재 내용 */}
            <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1.5, border: "1px solid #e2e8f0", mb: 2 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", mb: 1.5 }}>결재 내용</Typography>
                <FormFields type={approval.type} form={approval.form} applicant={approval.submitterName} />
            </Paper>

            {/* 첨부파일 */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: "1px solid #e2e8f0", mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b" }}>
                        첨부파일 ({approval.attachments?.length || 0})
                    </Typography>
                    {!done && (
                        <Tooltip title="파일 추가">
                            <IconButton size="small" disabled={uploading} onClick={() => fileInputRef.current?.click()} sx={{ p: 0.5 }}>
                                {uploading ? <CircularProgress size={14} /> : <AttachFileRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
                <input ref={fileInputRef} type="file" hidden multiple
                    onChange={e => { handleUpload(Array.from(e.target.files)); e.target.value = ""; }} />
                {approval.attachments?.length ? (
                    <Stack spacing={0.75}>
                        {approval.attachments.map(att => (
                            <Box key={att.id} sx={{ display: "flex", alignItems: "center", gap: 1, p: "8px 10px",
                                borderRadius: 1.5, border: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                                <Box sx={{ fontSize: 20, display: "flex" }}>{fileIcon(att.contentType)}</Box>
                                <Box flex={1} minWidth={0}>
                                    <Typography sx={{ fontSize: 12, fontWeight: 600 }} noWrap>{att.originalFileName}</Typography>
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{formatSize(att.fileSize)}</Typography>
                                </Box>
                                <Box component="a" href={att.fileUrl} target="_blank" rel="noreferrer">
                                    <DownloadRoundedIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                                </Box>
                                {att.uploadedAt && !done && (
                                    <IconButton size="small" onClick={() => handleDeleteAtt(att.id)} sx={{ p: 0.2 }}>
                                        <CloseRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                                    </IconButton>
                                )}
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Typography sx={{ fontSize: 13, color: "#94a3b8" }}>첨부파일 없음</Typography>
                )}
            </Paper>

            {/* 처리 완료 = 불변, 버튼 없음. ActionButton + 의미색 sx 오버라이드. */}
            {isMine && (
                <Stack direction="row" spacing={1.5}>
                    <ActionButton variant="primary" startIcon={<CheckCircleRoundedIcon />}
                        onClick={() => setActionDialog("approve")}
                        sx={{ flex: 1, py: 1.2, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
                        label={isFinalFin ? "결재 승인" : "승인"} />
                    <ActionButton variant="primary" startIcon={<CancelRoundedIcon />}
                        onClick={() => setActionDialog("reject")}
                        sx={{ flex: 1, py: 1.2, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }}
                        label="반려" />
                    <ActionButton variant="secondary" startIcon={<PauseCircleRoundedIcon />}
                        onClick={() => setActionDialog("hold")}
                        sx={{ flex: 1, py: 1.2, color: "#d97706", borderColor: "#fcd34d",
                            "&:hover": { bgcolor: "#fffbeb", borderColor: "#d97706" } }}
                        label="보류" />
                </Stack>
            )}

            {/* 처리 확인 서브 Dialog */}
            <Dialog open={Boolean(actionDialog)} onClose={() => setActionDialog(null)} maxWidth="xs" fullWidth>
                <Box sx={{ p: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>
                        {actionDialog === "approve" ? (isFinalFin ? "✅ 결재 승인 및 재무 반영" : "✅ 승인")
                         : actionDialog === "reject" ? "❌ 반려" : "⏸ 보류"} 하시겠습니까?
                    </Typography>
                    {actionDialog === "approve" && isFinalFin && (
                        <Typography sx={{ fontSize: 12, color: "#166534", bgcolor: "#f0fdf4",
                            border: "1px solid #bbf7d0", borderRadius: 1.5, p: 1, mb: 1.5 }}>
                            지출확정하면 완료(실적)로 등록됩니다{financeApprove ? " · 아래 계좌를 고르면 자금일보에도 자동 반영됩니다." : "."}
                        </Typography>
                    )}
                    {financeApprove && (
                        <Box sx={{ mb: 1.5 }}>
                            <FormField label="입출금 계좌 (자금일보 자동 반영)">
                                <ComboBox placeholder="계좌 선택 (선택 안 하면 자금일보 미반영)"
                                    options={accounts.map(a => ({ value: a.id, label: a.alias || a.bankName }))}
                                    value={confirmAccountId}
                                    onChange={v => setConfirmAccountId(v)} />
                            </FormField>
                        </Box>
                    )}
                    <TextField fullWidth multiline minRows={2} size="small" placeholder="의견 (선택)"
                        value={commentText} onChange={e => setCommentText(e.target.value)}
                        />
                    <Stack direction="row" spacing={1} mt={2} justifyContent="flex-end">
                        <ActionButton variant="cancel" onClick={() => setActionDialog(null)} />
                        <ActionButton variant="confirm" onClick={handleAction} startIcon={false}
                            sx={{
                                bgcolor: actionDialog === "approve" ? "#16a34a" : actionDialog === "reject" ? "#dc2626" : "#d97706",
                                "&:hover": { bgcolor: actionDialog === "approve" ? "#15803d" : actionDialog === "reject" ? "#b91c1c" : "#b45309" },
                            }} />
                    </Stack>
                </Box>
            </Dialog>
        </Box>
    );
}

/* ── ApprovalRow (한 줄) ─────────────────────────── */
const COL_BORDER = "1px solid rgba(0,0,0,0.06)";
const cell = (extra = {}) => ({
    px: 1.5, py: 0, display: "flex", alignItems: "center",
    borderRight: COL_BORDER, overflow: "hidden",
    ...extra,
});

function ApprovalRow({ approval, myId, selected, onClick }) {
    const m          = STATUS_META[approval.status] || STATUS_META.PENDING;
    const typeInfo   = FORM_TYPES.find(f => f.type === approval.type);
    const lineNames  = (approval.lines || []).map(l => l.approverName).join(" → ");
    const summary    = getRowSummary(approval.type, approval.form);
    const lastAction = [...(approval.lines || [])].filter(l => l.actionAt).pop();

    return (
        <Box onClick={onClick} sx={{
            display: "flex", alignItems: "stretch", cursor: "pointer", minHeight: 40,
            borderBottom: "1px solid #f1f5f9",
            borderLeft: selected ? "3px solid #2563eb" : "3px solid transparent",
            bgcolor: selected ? "#eff6ff" : "transparent",
            "&:hover": { bgcolor: selected ? "#eff6ff" : "#f8fafc" }, transition: "all 0.1s",
        }}>
            {/* 구분 */}
            <Box sx={{ ...cell(), flex: "0 0 110px" }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: m.color, mr: 0.75, flexShrink: 0 }} />
                {typeInfo && (
                    <Box sx={{ px: 0.6, py: 0.15, borderRadius: 0.75, fontSize: 10, fontWeight: 700,
                        bgcolor: `${typeInfo.color}18`, color: typeInfo.color, whiteSpace: "nowrap" }}>
                        {typeInfo.label}
                    </Box>
                )}
            </Box>

            {/* 제목 · 신청자 */}
            <Box sx={{ ...cell({ flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }),
                flex: "0 0 240px", gap: 0.2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b", width: "100%" }} noWrap>
                    {approval.title}
                </Typography>
                <Typography sx={{ fontSize: 11, color: "#94a3b8", width: "100%" }} noWrap>
                    {approval.submitterName}
                    {approval.submitterDept ? ` · ${approval.submitterDept}` : ""}
                </Typography>
            </Box>

            {/* 결재 라인 */}
            <Box sx={{ ...cell(), flex: 1 }}>
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }} noWrap>
                    {lineNames ? `→ ${lineNames}` : "—"}
                </Typography>
            </Box>

            {/* 요약 */}
            <Box sx={{ ...cell(), flex: "0 0 110px" }}>
                <Typography sx={{ fontSize: 12, color: "#475569", fontWeight: 600 }} noWrap>
                    {summary || "—"}
                </Typography>
            </Box>

            {/* 상태 */}
            <Box sx={{ ...cell(), flex: "0 0 68px", justifyContent: "center" }}>
                <Box sx={{ px: 0.75, py: 0.15, borderRadius: 0.75, fontSize: 11, fontWeight: 700,
                    bgcolor: m.bg, color: m.color, whiteSpace: "nowrap" }}>
                    {m.label}
                </Box>
            </Box>

            {/* 기안일 */}
            <Box sx={{ ...cell(), flex: "0 0 110px", justifyContent: "center" }}>
                <Typography sx={{ fontSize: 11, color: "#64748b", textAlign: "center" }}>
                    {dayjs(approval.createdAt).format("YYYY-MM-DD")}
                    <br />
                    <span style={{ fontSize: 10, color: "#94a3b8" }}>{dayjs(approval.createdAt).format("HH:mm")}</span>
                </Typography>
            </Box>

            {/* 마지막 결재자 */}
            <Box sx={{ ...cell(), flex: "0 0 100px" }}>
                <Typography sx={{ fontSize: 11, color: "#475569", fontWeight: lastAction ? 600 : 400 }} noWrap>
                    {lastAction ? lastAction.approverName : "—"}
                </Typography>
            </Box>

            {/* 결재일 */}
            <Box sx={{ ...cell({ borderRight: "none" }), flex: "0 0 110px", justifyContent: "center" }}>
                {lastAction ? (
                    <Typography sx={{ fontSize: 11, color: "#64748b", textAlign: "center" }}>
                        {dayjs(lastAction.actionAt).format("YYYY-MM-DD")}
                        <br />
                        <span style={{ fontSize: 10, color: "#94a3b8" }}>{dayjs(lastAction.actionAt).format("HH:mm")}</span>
                    </Typography>
                ) : (
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>—</Typography>
                )}
            </Box>
        </Box>
    );
}

/* ── Section ─────────────────────────────────────── */
function Section({ title, allItems, myId, selected, onSelect }) {
    const [page, setPage] = useState(1);
    const pages = Math.ceil(allItems.length / PAGE_SIZE) || 1;
    const slice = allItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Paper sx={ds.card}>
            <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>{title}</Typography>
                <Chip label={`${allItems.length}건`} size="small"
                    sx={{ bgcolor: "#f1f5f9", color: "#475569", fontWeight: 700 }} />
            </Box>
            <Divider />
            {allItems.length > 0 && (
                <Box sx={{ display: "flex", alignItems: "center",
                    bgcolor: "#f8fafc", borderBottom: "1px solid #e8ecf0",
                    borderLeft: "3px solid transparent" }}>
                    {[
                        { label: "구분",         w: "0 0 110px", center: true },
                        { label: "제목 · 신청자", w: "0 0 240px", center: true },
                        { label: "결재 라인",    w: 1, center: true },
                        { label: "요약",       w: "0 0 110px", center: true },
                        { label: "상태",       w: "0 0 68px", center: true },
                        { label: "기안일",     w: "0 0 110px", center: true },
                        { label: "마지막 결재자", w: "0 0 100px", center: true },
                        { label: "결재일",     w: "0 0 110px", center: true, last: true },
                    ].map((col) => (
                        <Box key={col.label} sx={{
                            flex: col.w, px: 1.5, py: 0.75,
                            borderRight: col.last ? "none" : COL_BORDER,
                            display: "flex", justifyContent: col.center ? "center" : "flex-start",
                        }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8" }}>
                                {col.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}
            {slice.length === 0 ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography color="text.secondary" fontSize={13}>항목이 없습니다.</Typography>
                </Box>
            ) : (
                <>
                    {slice.map(a => (
                        <ApprovalRow key={a.id} approval={a} myId={myId}
                            selected={selected?.id === a.id} onClick={() => onSelect(a)} />
                    ))}
                    {allItems.length > PAGE_SIZE && (
                        <TablePaginator
                            totalPages={pages}
                            page={page}
                            onPageChange={setPage}
                        />
                    )}
                </>
            )}
        </Paper>
    );
}

/* ── 메인 패널 (exported) ────────────────────────── */
export default function ApprovalIntegrationPanel({ loadApprovals, infoBannerText, financeConfirm, loadAccounts }) {
    const [approvals, setApprovals] = useState([]);
    const [myId,      setMyId]      = useState(null);
    const [loading,   setLoading]   = useState(true);
    const [selected,  setSelected]  = useState(null);
    const [accounts,  setAccounts]  = useState([]);   // 재무 지출확정 시 자금일보 반영 계좌

    const load = useCallback(() => {
        setLoading(true);
        loadApprovals()
            .then(data => setApprovals(Array.isArray(data) ? data : []))
            .catch(() => setApprovals([]))
            .finally(() => setLoading(false));
    }, [loadApprovals]);

    useEffect(() => {
        const token = localStorage.getItem("auth_token");
        if (token) api.get(`${BASE}/api/employees/me`)
            .then(r => setMyId(r.id)).catch(() => {});
        load();
    }, [load]);

    useEffect(() => {
        if (typeof loadAccounts === "function") {
            loadAccounts().then(d => setAccounts(Array.isArray(d) ? d : [])).catch(() => {});
        }
    }, [loadAccounts]);

    const pending  = approvals.filter(a => !isCompleted(a));
    const done     = approvals.filter(a => isCompleted(a));

    const handleClose   = () => setSelected(null);
    const handleRefresh = () => load();

    if (loading) return <LoadingDots />;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Paper sx={{ ...ds.cardPadded, bgcolor: "#fffbeb", border: "1px solid #fde68a" }}>
                <Typography sx={{ fontSize: 12, color: "#92400e" }}>{infoBannerText}</Typography>
            </Paper>

            <Section title="처리 대기 중" allItems={pending} myId={myId} selected={selected} onSelect={setSelected} />
            <Section title="처리 완료"    allItems={done}    myId={myId} selected={selected} onSelect={setSelected} />

            <Dialog open={!!selected} onClose={handleClose} maxWidth="md" fullWidth>
                <Box sx={{ display: "flex", alignItems: "center", px: 3, pt: 2, pb: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#64748b", flex: 1 }}>결재 상세</Typography>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
                <DialogContent sx={{ pt: 2 }}>
                    {selected && (
                        <ApprovalDetail approval={selected} myId={myId}
                            onRefresh={handleRefresh} onClose={handleClose}
                            financeConfirm={financeConfirm} accounts={accounts} />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
